import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { sessionQueryKey } from '../../lib/session';

/** Client side of the auth routes (docs/dev/api-shell.md "Auth"). */

/** M8: `user_setup` / `organization_setup` start the wizard of a setup session (api-shell „SessionInfo (M8)“). */
export type NextStep = 'two_factor' | 'two_factor_setup' | 'workspace' | 'user_setup' | 'organization_setup';

export interface LoginResponse {
  next: NextStep;
}

export interface LockDetails {
  until: string;
  lockedSince: string;
  adminName: string | null;
  email: string;
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
  keyGroups: string[];
}

export interface RecoveryCodesResponse {
  recoveryCodes: string[];
}

export const PASSWORD_POLICY_HINT = 'mindestens 12 Zeichen, Groß- und Kleinbuchstaben, eine Zahl';
export const PASSWORD_MISMATCH = 'Die beiden Eingaben stimmen nicht überein.';

export function routeForNext(next: NextStep, fallback = '/home'): string {
  switch (next) {
    case 'two_factor':
      return '/login/2fa';
    case 'two_factor_setup':
      return '/login/2fa/setup';
    case 'user_setup':
      return '/setup/user';
    case 'organization_setup':
      return '/setup/organization';
    case 'workspace':
      return fallback;
  }
}

/** The 423 payload of a login attempt (Shell 19), or null for any other error. */
export function lockDetailsOf(error: unknown): LockDetails | null {
  if (!(error instanceof ApiError) || error.status !== 423) return null;
  const until = error.detailString('until');
  const lockedSince = error.detailString('lockedSince');
  const email = error.detailString('email');
  if (until === null || lockedSince === null || email === null) return null;
  return { until, lockedSince, adminName: error.detailString('adminName'), email };
}

/** "2 von 5 Versuchen genutzt" from `details.attemptsUsed`/`attemptsMax`, or null. */
export function attemptsText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const used = error.detailNumber('attemptsUsed');
  const max = error.detailNumber('attemptsMax');
  if (used === null || max === null) return null;
  return `${used} von ${max} Versuchen genutzt`;
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST', body: {} }),
    onSettled: () => {
      queryClient.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api<void>('/auth/forgot-password', { method: 'POST', body: { email } }),
  });
}

export function invalidateSession(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: sessionQueryKey });
}
